import Button from '@atoms/Button';
import { VARIANT } from '@atoms/Button/Button.types';
import type { ReactNode } from 'react';
import styled, { css, keyframes } from 'styled-components';

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column-reverse;
  gap: 16px;

  @media ${({ theme }) => theme.media.desktop} {
    flex-direction: row;
    gap: 32px;
    margin-top: 42px;
  }
`;

export const ContentColumn = styled.div`
  flex: 1;
  align-self: start;
`;

export const ImageColumn = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const Title = styled.h1`
  font-size: 40px;
  line-height: 1.2;
  margin-bottom: 24px;
  text-align: center;

  @media ${({ theme }) => theme.media.tablet} {
    font-size: 48px;
    text-align: start;
  }

  @media ${({ theme }) => theme.media.desktop} {
    font-size: 58px;
  }
`;

export const TitleLine = styled.span`
  display: block;
`;

export const Subtitle = styled.p`
  font-size: 18px;
  color: #444;
  text-align: center;
  margin-bottom: 16px;

  @media ${({ theme }) => theme.media.tablet} {
    text-align: start;
  }

  @media ${({ theme }) => theme.media.desktop} {
    margin-bottom: 30px;
  }
`;

export const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 16px;
  margin-top: 38px;
  margin-bottom: 42px;

  @media ${({ theme }) => theme.media.tablet} {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: initial;
    justify-content: center;
  }

  @media ${({ theme }) => theme.media.desktop} {
    justify-content: flex-start;
  }
`;

export const CtaButton = styled(Button)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 50px;
  gap: 10px;
  padding: 0 52px;

  ${({ variant }) =>
    variant !== VARIANT.SECONDARY &&
    css`
      padding: 0 64px;
    `};
`;

export const ArrowIcon = styled.svg<{
  children?: ReactNode;
  viewBox?: string;
}>`
  width: 28px;
  height: 10px;
  flex-shrink: 0;
  fill: none;
`;

export const StatsMiniWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 30px;
`;

export const StatsMiniItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: left;
`;

export const StatsMiniIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  font-size: 18px;
  background-color: rgba(247, 127, 0, 0.12);
  border-radius: 50%;
  flex-shrink: 0;
`;

export const StatsMiniText = styled.p`
  margin: 0;
  font-size: 14px;
  color: #444;
`;

export const StatsMiniNumber = styled.strong`
  font-weight: 700;
`;

export const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 10/13;
  background-color: ${({ theme }) => theme.palette.lightGray};
  border-radius: 8px;
  overflow: hidden;
`;

export const HeroQuoteCard = styled.div`
  position: absolute;
  right: 6px;
  bottom: 12px;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 200px;
  padding: 10px 12px;
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.1);

  @media ${({ theme }) => theme.media.tablet} {
    right: 12px;
    bottom: 20px;
    width: 220px;
    padding: 14px 16px;
    gap: 8px;
  }
`;

export const HeroQuoteHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const HeroQuoteAvatar = styled.div`
  position: relative;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;

  @media ${({ theme }) => theme.media.tablet} {
    width: 34px;
    height: 34px;
  }
`;

export const HeroQuoteMeta = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.3;
  min-width: 0;
`;

export const HeroQuoteAuthor = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: #333;
  white-space: nowrap;

  @media ${({ theme }) => theme.media.tablet} {
    font-size: 13px;
  }
`;

export const HeroQuoteRating = styled.span`
  font-size: 10px;
  color: #ffd700;
  letter-spacing: 1px;

  @media ${({ theme }) => theme.media.tablet} {
    font-size: 12px;
  }
`;

export const HeroQuoteText = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 1.4;
  color: #444;
  font-style: italic;

  @media ${({ theme }) => theme.media.tablet} {
    font-size: 13px;
  }
`;

const highlightSweep = keyframes`
  to {
    background-size: 100% 48%;
  }
`;

const HIGHLIGHT_LINE_DURATION = 0.5;
const HIGHLIGHT_START_DELAY = 1.5;

export const HeroQuoteHighlight = styled.span<{
  children?: ReactNode;
  lineIndex: 0 | 1;
}>`
  background-image: linear-gradient(
    rgba(240, 152, 25, 0.35),
    rgba(240, 152, 25, 0.35)
  );
  background-repeat: no-repeat;
  background-position: 0 90%;
  background-size: 0% 48%;
  border-radius: 3px;
  box-decoration-break: clone;
  -webkit-box-decoration-break: clone;
  animation: ${highlightSweep} ${HIGHLIGHT_LINE_DURATION}s ease-out forwards;
  animation-delay: ${({ lineIndex }) =>
    HIGHLIGHT_START_DELAY + lineIndex * HIGHLIGHT_LINE_DURATION}s;
`;

export const TrustBadgesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 24px;
  border-top: 1px solid ${({ theme }) => theme.palette.lightGray};

  @media ${({ theme }) => theme.media.tablet} {
    flex-direction: row;
    gap: 12px;
  }
`;

export const TrustBadgeItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const TrustBadgeIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  font-size: 22px;
  background-color: #fff;
  border: 1px solid ${({ theme }) => theme.palette.lightGray};
  border-radius: 50%;
  flex-shrink: 0;
`;

export const TrustBadgeLabel = styled.p`
  font-size: 13px;
  font-weight: 400;
  color: #444;
  line-height: 1.3;
`;
